import { DocumentCategory, DocumentTypes } from "@prisma/client";

// Serialized regex shapes stored in DocumentTypeDefinition.classifier (see
// PrismaJson.DocumentTypeClassifier). Declared locally so the package stays
// self-contained.
export type SerializedRegex = {
  source: string;
  flags: string;
};

export type SerializedPattern = SerializedRegex & {
  weight: number;
};

export type SerializedClassifier = {
  patterns: SerializedPattern[];
  exclusionPatterns?: SerializedRegex[];
  filenamePatterns?: SerializedRegex[];
};

// Cloudinary reference to a sample document uploaded during pattern
// generation (see PrismaJson.DocumentTypeSampleDocuments).
export type SampleDocumentRef = {
  fileName: string;
  publicId: string;
  secureUrl: string;
  format?: string;
  // Detection score against the type's classifier as of the save that stored
  // this sample — the persisted health signal for management surfaces.
  // Absent on samples saved before scores were recorded.
  score?: number;
  // The extracted text the sample was scored with (truncated), persisted so
  // later sessions reuse it instead of re-extracting — re-extraction can
  // differ (OCR vs text-layer) and silently flip a sample unreadable.
  // Absent on samples saved before text was recorded, and on unreadable
  // scans kept as debugging aids.
  text?: string;
};

// Admin-defined fields the AI extracts from uploads of this type (see
// PrismaJson.DocumentTypeExtractionFields). Declared locally so the package
// stays self-contained.
export type ExtractionFieldDataType = "text" | "number" | "date" | "currency";

export type DocumentTypeExtractionField = {
  // Immutable once persisted — stored Document.extractedData anchors to it.
  key: string;
  label: string;
  // Prompt guidance for the AI; falls back to the label when absent.
  instruction?: string;
  dataType: ExtractionFieldDataType;
  // Soft requirement: verification UI highlights missing values, never blocks.
  required?: boolean;
};

// Extracted values stored on Document.extractedData, keyed by field key (see
// PrismaJson.DocumentExtractedData). All values are strings — the field's
// dataType drives prompt format rules and display affordances, not storage.
// Absent key = never extracted; clearing a value deletes the key.
export type DocumentExtractedData = Record<string, string>;

export type DocumentTypeDefinitionData = {
  key: string;
  label: string;
  category: DocumentCategory;
  isBuiltIn: boolean;
  active: boolean;
  classifier: SerializedClassifier | null;
  sampleDocuments?: SampleDocumentRef[] | null;
  extractionFields?: DocumentTypeExtractionField[] | null;
};

const p = (pattern: RegExp, weight: number): SerializedPattern => ({
  source: pattern.source,
  flags: pattern.flags,
  weight,
});

const r = (pattern: RegExp): SerializedRegex => ({
  source: pattern.source,
  flags: pattern.flags,
});

/**
 * The seven built-in document types with their upload-time classifiers,
 * migrated verbatim from the old hardcoded DOCUMENT_PATTERNS scorer. These
 * seed the DocumentTypeDefinition collection (seed-document-types script) and
 * act as the in-code fallback until the seed has run.
 */
export const BUILT_IN_DOCUMENT_TYPES: DocumentTypeDefinitionData[] = [
  {
    key: DocumentTypes.W9,
    label: "W9",
    category: DocumentCategory.VENDOR_COMPLIANCE,
    isBuiltIn: true,
    active: true,
    classifier: {
      patterns: [
        // Strong W-9 indicators
        p(/form\s+w-?9/i, 5),
        p(
          /request\s+for\s+taxpayer\s+identification\s+number(?:\s+and\s+certification)?/i,
          5
        ),
        // Tax identification patterns
        p(/taxpayer\s+identification\s+number/i, 4),
        p(/employer\s+identification\s+number/i, 4),
        p(/social\s+security\s+number/i, 4),
        // IRS/Treasury patterns
        p(/backup\s+withholding/i, 3),
        p(/department\s+of\s+the\s+treasury/i, 3),
        p(/internal\s+revenue\s+service/i, 3),
        // Proximity pattern - certification near W-9 context
        p(
          /certification[\s\S]{0,200}(?:backup\s+withholding|taxpayer\s+identification|form\s+w-?9|internal\s+revenue\s+service)/i,
          2
        ),
      ],
      filenamePatterns: [r(/w-?9/i)],
    },
  },
  {
    key: DocumentTypes.BUSINESS_LICENSE,
    label: "Business License",
    category: DocumentCategory.VENDOR_COMPLIANCE,
    isBuiltIn: true,
    active: true,
    classifier: {
      patterns: [
        p(/business\s+license/i, 10),
        p(/license\s+number/i, 8),
        p(/certificate\s+of\s+incorporation/i, 10),
        p(/occupational\s+tax/i, 8),
        p(/tax\s+certificate/i, 8),
        p(/\blicense\b/i, 2),
        p(/\bpermit\b/i, 2),
        p(/\bcertificate\b/i, 2),
        p(/\bregistration\b/i, 2),
        p(/\bexpires?\b/i, 2),
        p(/permit\s+#/i, 5),
      ],
      filenamePatterns: [r(/license/i)],
    },
  },
  {
    key: DocumentTypes.INVOICE,
    label: "Invoice",
    category: DocumentCategory.PROJECT_EXPENSE,
    isBuiltIn: true,
    active: true,
    classifier: {
      patterns: [
        // Strong invoice indicators
        p(/\binvoice\b/i, 2),
        p(/final\s+invoice/i, 4),
        p(/original\s+invoice/i, 4),
        p(/invoice\s+(?:number|no\.?|#)/i, 3),
        p(/invoice\s*#/i, 3),
        p(/(^|[^a-z0-9])inv\s*#/i, 3),
        p(/invoice\s+date/i, 3),
        p(/invoice\s+amount/i, 3),
        p(/invoice\s+payable\s+to/i, 3),
        // Receipt pattern (keeping separate from RECEIPT type)
        p(/\breceipt\b/i, 2),
        // Business terms
        p(/bill\s+to/i, 1),
        p(/sold\s+to/i, 1),
        p(/ship\s+to(?:\s+address)?/i, 1),
        p(/\bcustomer\b/i, 1),
        p(/customer\s+(?:no\.?|#|nno\.)/i, 1),
        p(/remit\s+to/i, 1),
        // Payment terms
        p(/payment\s+terms/i, 1),
        p(/due\s+date/i, 1),
        p(/amount\s+due/i, 2),
        p(/total\s+amount\s+due/i, 3),
        p(/balance\s+due/i, 2),
        // Totals and amounts
        p(/grand\s+total/i, 2),
        p(/(^|[^a-z0-9])total\s*:?(?!\s+pages)/i, 1),
        p(/total\s+charges/i, 2),
        p(/sub\s*total|subtotal/i, 1),
        p(/sales\s+tax/i, 1),
        p(/(^|[^a-z0-9])tax([^a-z0-9]|$)/i, 1),
        p(/transaction\s+fee/i, 1),
        // Line item patterns
        p(/description[\s\S]{0,160}amount|amount[\s\S]{0,160}description/i, 2),
        p(/description[\s\S]{0,160}price|price[\s\S]{0,160}description/i, 2),
        p(/(^|[^a-z0-9])qty([^a-z0-9]|$)|quantity/i, 1),
        p(/(^|[^a-z0-9])units?([^a-z0-9]|$)/i, 1),
        p(/unit\s+price/i, 1),
        p(/(^|[^a-z0-9])amount([^a-z0-9]|$)/i, 1),
        // Rental specific
        p(/rental\s+period/i, 1),
        p(/rental\s+charges/i, 2),
        // Payment instructions
        p(/make\s+(?:all\s+)?checks\s+payable/i, 2),
        p(/thank\s+you\s+for\s+your\s+business/i, 1),
        // OCR error patterns - common misreads
        p(/\b(?:imvoice|nvoice|1nvoice|invo1ce)\b/i, 2),
        p(/(?:soles\s+tox|s0les\s+t0x)/i, 1),
        p(/\btotaldue\b/i, 2),
        p(/\bdueuponreceipt\b/i, 2),
        p(/\bmakechecksto\b/i, 2),
        p(/\bcostper\b/i, 1),
        p(/\bbillto\b/i, 1),
        p(/\bshipto\b/i, 1),
        p(/customer\s+nno/i, 1),
      ],
      exclusionPatterns: [
        r(/invoice\s+total\s+pages/i),
        // NOTE: Do not exclude on a bare "Page X of Y" footer — that appears
        // on virtually every real invoice and was causing legitimate invoices
        // to be classified as OTHER. Keep the narrower guard only.
        // Exclude payroll/labor documents
        r(/payroll\s+register/i),
        r(/extras\s+voucher/i),
        r(/hours\s+worked/i),
        r(/start\s+work/i),
        r(/finish\s+work/i),
        // Exclude agreements
        r(/terms\s+and\s+conditions/i),
        r(/electronic\s+record\s+and\s+signature\s+disclosure/i),
        r(/docusign/i),
      ],
      filenamePatterns: [r(/invoice/i)],
    },
  },
  {
    key: DocumentTypes.RECEIPT,
    label: "Receipt",
    category: DocumentCategory.PROJECT_EXPENSE,
    isBuiltIn: true,
    active: true,
    classifier: {
      patterns: [
        p(/\breceipt\b/i, 10),
        p(/transaction\s+(?:id|number)/i, 8),
        p(/thank\s+you\s+for\s+your\s+purchase/i, 8),
        p(/\bpurchase\b/i, 5),
        p(/payment\s+received/i, 8),
        p(/\bpaid\b/i, 3),
        p(/\btransaction\b/i, 3),
        p(/receipt\s+#?\d+/i, 8),
      ],
      filenamePatterns: [r(/receipt/i)],
    },
  },
  {
    key: DocumentTypes.PROOF_OF_PAYMENT,
    label: "Proof of Payment",
    category: DocumentCategory.PROJECT_EXPENSE,
    isBuiltIn: true,
    active: true,
    classifier: {
      patterns: [
        p(/payment\s+confirmation/i, 10),
        p(/wire\s+transfer/i, 10),
        p(/funds\s+transferred/i, 10),
        p(/payment\s+received/i, 8),
        // Check request patterns
        p(/company\s+check\s+request/i, 8),
        p(/check\s+request/i, 6),
        p(/check\s+total/i, 5),
        p(/paid\s+by\s+check/i, 6),
        p(/date\s+check\s+required/i, 5),
        p(/accounting\s+use\s+only/i, 3),
        p(/approvals?/i, 2),
        // General payment patterns
        p(/\bpayment\b/i, 2),
        p(/\btransfer\b/i, 2),
        p(/\bconfirmation\b/i, 2),
        p(/reference\s+number/i, 5),
      ],
      filenamePatterns: [r(/payment|transfer/i)],
    },
  },
  {
    // OTHER stays the code-level ambiguous fallback (its category depends on
    // upload scope); the seeded category is only a placeholder.
    key: DocumentTypes.OTHER,
    label: "Other",
    category: DocumentCategory.PROJECT_EXPENSE,
    isBuiltIn: true,
    active: true,
    classifier: null,
  },
  {
    key: DocumentTypes.CHECKLIST_DOCUMENT,
    label: "Checklist Document",
    category: DocumentCategory.CHECKLIST,
    isBuiltIn: true,
    active: true,
    classifier: null,
  },
];

// Fallback vendor-compliance whitelist for contexts where the registry can't
// be read (matches the pre-registry behavior).
export const DEFAULT_VENDOR_DOC_TYPE_KEYS: string[] = [
  DocumentTypes.BUSINESS_LICENSE,
  DocumentTypes.W9,
];

/**
 * Merges DB rows with the code built-ins — the bootstrap for a registry the
 * seed script hasn't reached yet (fresh databases, tests, code defaults).
 * DB rows win on key collision (they may carry admin-edited labels and
 * classifiers). Injection is all-or-nothing on seed evidence: once any
 * seeded standard row (isBuiltIn) is present, nothing is injected — a
 * standard type an admin deliberately deleted must stay deleted, not
 * resurrect from code on the next read. (Re-running the seed script does
 * restore deleted standard types — the recovery hatch.) Pure.
 */
export const mergeWithBuiltIns = <
  T extends { key: string; isBuiltIn?: boolean },
>(
  dbRows: T[]
): (T | DocumentTypeDefinitionData)[] => {
  if (dbRows.some((row) => row.isBuiltIn)) return dbRows;
  const dbKeys = new Set(dbRows.map((row) => row.key));
  const missingBuiltIns = BUILT_IN_DOCUMENT_TYPES.filter(
    (builtIn) => !dbKeys.has(builtIn.key)
  );
  return [...dbRows, ...missingBuiltIns];
};

/**
 * Built-in types the system itself depends on: OTHER is the detection
 * fallback bucket for unrecognized documents, and CHECKLIST_DOCUMENT anchors
 * the checklist surfaces. They can never be deactivated, and their category
 * is fixed (the other built-ins may be toggled and re-categorized — reaching
 * a global row's update already requires admin-org membership).
 */
export const isProtectedDocumentTypeKey = (key: string): boolean =>
  key === DocumentTypes.OTHER || key === DocumentTypes.CHECKLIST_DOCUMENT;

/**
 * Drops a sample's persisted text (up to 12k chars each, 10 samples per row)
 * for list reads that only need the score and count. Pure.
 */
export const slimSampleDocument = ({
  text: _text,
  ...rest
}: SampleDocumentRef): SampleDocumentRef => rest;
